import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  Merchant,
  MerchantFilter,
  ActionRequest,
  ActionType,
  AvailableAction,
  EventTypes,
  MerchantEventTolerances,
  AlertHistory,
  BulkActionsRequest,
  ReserveMetrics,
} from '@/types/fcc'
import { filterMerchant } from '@/util/fcc/FCCUtil'
import { useFCCApi } from '@/services/fraudCommandCenterService'
import { netsuiteVendorApi } from '@/services/netsuiteVendorService'
import { AuthState } from '@/types/authState'
import { FCCApiParams } from '@/types/fcc'
import { ApiError } from '@/util/ApiError'

export const useFCCMerchantStore = defineStore('fccMerchant', () => {
  // State
  const merchants = ref<Merchant[]>([])
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  const filters = ref<MerchantFilter>({
    owner: {
      selected: [],
      options: [],
    },
    platform: {
      selected: [],
      options: [],
    },
    tolerance: {
      selected: [],
      options: [],
    },
    search: {
      value: '',
    },
  })

  const currentMerchantId = ref<number | null>(null)
  const showMerchantDetails = ref(false)
  const isEditModalOpen = ref(false)
  const isEditReservesModalOpen = ref(false)
  const eventTypes = ref<EventTypes>([])
  const selectedMerchants = ref<number[]>([])
  const selectedMerchant = ref<Merchant | null>(null)
  const merchantEventTolerance = ref('Medium')
  const currentMerchantEventTolerances = ref<string[]>([])
  const availableActions = ref<AvailableAction[]>([])
  const lastActionCompleted = ref<string | null>(null)
  const merchantAlertHistory = ref<AlertHistory[]>([])
  const alertHistoryPagination = ref({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  })
  const alertHistoryLoading = ref(false)
  // reserveMetrics: a 200 record (in reserve table) or null (404 / not in
  // table OR not yet fetched — disambiguated by reserveMetricsLoaded).
  const reserveMetrics = ref<ReserveMetrics | null>(null)
  const reserveMetricsLoading = ref(false)
  // True once a fetch has settled successfully (200 or 404). Lets callers
  // tell "loaded, not in table" (null) apart from "not fetched yet" (null).
  const reserveMetricsLoaded = ref(false)
  // True only on a genuine fetch failure (5xx / network) — not a 404.
  const reserveMetricsError = ref(false)

  const isManageSettlementsModalOpen = ref(false)
  // The supplier's NetSuite settlement-hold flag, null until fetched or when
  // the fetch failed.
  const holdPayment = ref<boolean | null>(null)
  const holdPaymentLoading = ref(false)
  // The vendor the newest read was issued for, so an older one can tell that
  // it no longer owns the state by the time it settles.
  const inFlightVendorName = ref<string | null>(null)

  // API utilities
  const {
    getMerchants,
    getMerchantDetails,
    getMerchantAlertHistory,
    getMerchantEventTolerances,
    updateMerchantEventTolerance,
    getEventTypes,
    getAvailableActions,
    updateTolerance,
    enableReserve,
    getMerchantReserveMetrics,
    setReservePersona,
    setReserveManual,
    updateMaxAmount,
    updatePercentHoldback,
    executeAction,
    executeBulkActions: executeBulkActionsApi,
  } = useFCCApi()

  // Getters (computed values)
  const filteredMerchants = computed((): Merchant[] => {
    return merchants.value.filter(merchant =>
      filterMerchant(merchant, filters.value),
    )
  })

  const currentMerchant = computed((): Merchant | null => {
    if (!currentMerchantId.value) return null
    return (
      merchants.value.find(
        merchant => merchant.MERCHANT_ID === currentMerchantId.value,
      ) || null
    )
  })

  const currentMerchantName = computed((): string => {
    return currentMerchant.value?.MERCHANT_NAME || ''
  })

  const getMerchantById = computed(
    () =>
      (merchantId: number): Merchant | null => {
        return (
          merchants.value.find(
            merchant => merchant && merchant.MERCHANT_ID === merchantId,
          ) || null
        )
      },
  )

  // Actions (mutations)
  const setMerchants = (newMerchants: Merchant[]): void => {
    merchants.value = newMerchants
  }

  const setCurrentMerchantId = (merchantId: number | null): void => {
    currentMerchantId.value = merchantId
  }

  const setShowMerchantDetails = (show: boolean): void => {
    showMerchantDetails.value = show
  }

  const setIsEditModalOpen = (isOpen: boolean): void => {
    isEditModalOpen.value = isOpen
  }

  const setIsEditReservesModalOpen = (isOpen: boolean): void => {
    isEditReservesModalOpen.value = isOpen
  }

  const setIsManageSettlementsModalOpen = (isOpen: boolean): void => {
    isManageSettlementsModalOpen.value = isOpen
  }

  const setLoading = (isLoading: boolean): void => {
    loading.value = isLoading
  }

  const setEventTypes = (types: EventTypes): void => {
    eventTypes.value = types
  }

  const setAvailableActions = (actions: AvailableAction[]): void => {
    availableActions.value = actions
  }

  const setSelectedMerchants = (merchantIds: number[]): void => {
    selectedMerchants.value = merchantIds
  }

  const setSelectedMerchant = (merchant: Merchant | null): void => {
    selectedMerchant.value = merchant
  }

  const setMerchantEventTolerance = (tolerance: string): void => {
    merchantEventTolerance.value = tolerance
  }

  const setCurrentMerchantEventTolerances = (tolerances: string[]): void => {
    currentMerchantEventTolerances.value = tolerances
  }

  const setLastActionCompleted = (action: string | null): void => {
    lastActionCompleted.value = action
  }

  const updateFilters = (newFilters: Partial<MerchantFilter>): void => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const resetFilters = (): void => {
    filters.value = {
      owner: {
        selected: [],
        options: filters.value.owner.options,
      },
      platform: {
        selected: [],
        options: filters.value.platform.options,
      },
      tolerance: {
        selected: [],
        options: filters.value.tolerance.options,
      },
      search: {
        value: '',
      },
    }
  }

  // API calls (actions)
  const fetchMerchants = async (
    authState: AuthState,
    params?: FCCApiParams,
  ): Promise<Merchant[]> => {
    loading.value = true
    error.value = null
    try {
      const fetchedMerchants: Merchant[] = await getMerchants(authState, params)
      merchants.value = fetchedMerchants
      return fetchedMerchants
    } catch (err) {
      error.value = ApiError.isApiError(err) ? err : null
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchMerchantDetails = async (
    authState: AuthState,
    merchantId: number,
  ): Promise<Merchant> => {
    loading.value = true
    try {
      const merchantDetails = await getMerchantDetails(
        authState,
        merchantId.toString(),
      )

      // Ensure we have valid merchant details before proceeding
      if (!merchantDetails || !merchantDetails.MERCHANT_ID) {
        console.error('Invalid merchant details received:', merchantDetails)
        throw new Error('Invalid merchant details received from API')
      }

      // Update the merchant in the list or add it if not present
      const index = merchants.value.findIndex(
        m => m && m.MERCHANT_ID === merchantId,
      )
      if (index >= 0) {
        merchants.value[index] = {
          ...merchants.value[index],
          ...merchantDetails,
        }
      } else {
        merchants.value.push(merchantDetails)
      }
      return merchantDetails
    } catch (error) {
      console.error('Error fetching merchant details:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchMerchantAlertHistory = async (
    authState: AuthState,
    merchantId: number,
    page = 1,
    pageSize = 20,
  ): Promise<AlertHistory[]> => {
    alertHistoryLoading.value = true
    try {
      const result = await getMerchantAlertHistory(
        authState,
        merchantId.toString(),
        page,
        pageSize,
      )
      merchantAlertHistory.value = result.alerts
      alertHistoryPagination.value = {
        page: result.pagination.page,
        pageSize: result.pagination.pageSize,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages,
      }
      return result.alerts
    } catch (error) {
      console.error('Error fetching merchant alert history:', error)
      throw error
    } finally {
      alertHistoryLoading.value = false
    }
  }

  const fetchMerchantEventTolerances = async (
    authState: AuthState,
    merchantId: number,
  ): Promise<MerchantEventTolerances> => {
    try {
      const response = await getMerchantEventTolerances(
        authState,
        merchantId.toString(),
      )
      currentMerchantEventTolerances.value =
        response?.EVENT_TOLERANCES?.map(et => et.TOLERANCE) ?? []
      if (response?.MERCHANT_TOLERANCE) {
        merchantEventTolerance.value = response.MERCHANT_TOLERANCE
      }
      return response
    } catch (error) {
      console.error('Error fetching merchant event tolerances:', error)
      currentMerchantEventTolerances.value = []
      throw error
    }
  }

  const updateMerchantEventToleranceValue = async (
    authState: AuthState,
    merchantId: number,
    eventType: string,
    tolerance: string,
  ): Promise<void> => {
    try {
      await updateMerchantEventTolerance(
        authState,
        merchantId.toString(),
        eventType,
        tolerance,
      )
    } catch (error) {
      console.error('Error updating merchant event tolerance:', error)
      throw error
    }
  }

  const fetchEventTypes = async (authState: AuthState): Promise<void> => {
    try {
      const types = await getEventTypes(authState)
      // for some reason, the API returns a 0 EVENT_TYPE_ID item which is totally blank/useless, so we need to filter it out
      eventTypes.value = types.filter(type => type.EVENT_TYPE_ID !== 0)
    } catch (error) {
      console.error('Error fetching event types:', error)
      throw error
    }
  }

  const fetchAvailableActions = async (authState: AuthState): Promise<void> => {
    try {
      const actions = await getAvailableActions(authState)
      availableActions.value = actions
    } catch (error) {
      console.error('Error fetching available actions:', error)
      throw error
    }
  }

  const updateMerchantTolerance = async (
    authState: AuthState,
    merchantId: number,
    tolerance: string,
  ): Promise<void> => {
    try {
      await updateTolerance(authState, merchantId.toString(), tolerance)
      // Update the merchant in the list
      const index = merchants.value.findIndex(m => m.MERCHANT_ID === merchantId)
      if (index >= 0) {
        merchants.value[index].TOLERANCE_ID = tolerance
      }
    } catch (error) {
      console.error('Error updating merchant tolerance:', error)
      throw error
    }
  }

  const updateMerchantMaxAmount = async (
    authState: AuthState,
    merchantId: number,
    maxAmount: number,
  ): Promise<void> => {
    try {
      await updateMaxAmount(authState, merchantId, maxAmount)
      // Update the merchant in the list if current merchant matches
      if (currentMerchantId.value) {
        const index = merchants.value.findIndex(
          m => m.MERCHANT_ID === currentMerchantId.value,
        )
        if (index >= 0) {
          merchants.value[index].MAX_AMOUNT = maxAmount
        }
      }
    } catch (error) {
      console.error('Error updating merchant max amount:', error)
      throw error
    }
  }

  const enableMerchantReserve = async (
    authState: AuthState,
    merchantId: number,
    payload?: { percent_holdback: number; max_amount: number },
  ): Promise<{ message: string }> => {
    try {
      return await enableReserve(authState, merchantId, payload)
    } catch (error) {
      console.error('Error enabling merchant reserve:', error)
      throw error
    }
  }

  const setMerchantReservePersona = async (
    authState: AuthState,
    merchantId: number,
    payload: { persona_case_id: string; website_number: number },
  ): Promise<{ message?: string }> => {
    try {
      return await setReservePersona(authState, merchantId, payload)
    } catch (error) {
      console.error('Error setting merchant reserve via Persona:', error)
      throw error
    }
  }

  const setMerchantReserveManual = async (
    authState: AuthState,
    merchantId: number,
    payload: { percent_holdback: number; max_amount: number },
  ): Promise<{ message?: string }> => {
    try {
      return await setReserveManual(authState, merchantId, payload)
    } catch (error) {
      console.error('Error setting merchant reserve manually:', error)
      throw error
    }
  }

  const updateMerchantPercentHoldback = async (
    authState: AuthState,
    merchantId: number,
    percentHoldback: number,
  ): Promise<{ message?: string }> => {
    try {
      return await updatePercentHoldback(authState, merchantId, {
        percent_holdback: percentHoldback,
      })
    } catch (error) {
      console.error('Error updating merchant percent holdback:', error)
      throw error
    }
  }

  const fetchMerchantReserveMetrics = async (
    authState: AuthState,
    merchantId: number,
  ): Promise<ReserveMetrics | null> => {
    reserveMetricsLoading.value = true
    reserveMetricsError.value = false
    try {
      // 200 → metrics object (in table); 404 → null (not in table). Both are
      // successful settles, so mark loaded either way.
      const metrics = await getMerchantReserveMetrics(authState, merchantId)
      reserveMetrics.value = metrics
      reserveMetricsLoaded.value = true
      return metrics
    } catch (error) {
      // Genuine failure (5xx / network) — distinct from a 404 "not in table".
      console.error('Error fetching merchant reserve metrics:', error)
      reserveMetrics.value = null
      reserveMetricsError.value = true
      throw error
    } finally {
      reserveMetricsLoading.value = false
    }
  }

  const clearReserveMetrics = (): void => {
    reserveMetrics.value = null
    reserveMetricsLoaded.value = false
    reserveMetricsError.value = false
  }

  // Not in Snowflake, so it needs its own call whenever a merchant is opened.
  //
  // The vendor name the caller asked for is captured up front and rechecked on
  // settle: switching merchants mid-flight would otherwise let the first
  // response land on the second merchant's tile. A stale response returns its
  // value to its own caller but writes nothing.
  const fetchVendorHoldPayment = async (
    authState: AuthState,
    vendorName: string,
  ): Promise<boolean | null> => {
    inFlightVendorName.value = vendorName
    holdPaymentLoading.value = true
    try {
      const vendor = await netsuiteVendorApi.getVendor(authState, vendorName)
      if (inFlightVendorName.value !== vendorName) return vendor.PaymentHold
      holdPayment.value = vendor.PaymentHold
      return vendor.PaymentHold
    } catch (error) {
      console.error('Error fetching NetSuite vendor hold payment:', error)
      if (inFlightVendorName.value !== vendorName) throw error
      holdPayment.value = null
      throw error
    } finally {
      if (inFlightVendorName.value === vendorName) {
        holdPaymentLoading.value = false
      }
    }
  }

  // Throws on failure so the dialog can surface a toast instead of closing.
  const updateVendorHoldPayment = async (
    authState: AuthState,
    vendorName: string,
    hold: boolean,
  ): Promise<void> => {
    try {
      await netsuiteVendorApi.updateHoldPayment(authState, vendorName, hold)
      // PATCH may answer 204, so trust the value just written. Caller refetches.
      holdPayment.value = hold
    } catch (error) {
      console.error('Error updating NetSuite vendor hold payment:', error)
      throw error
    }
  }

  const clearVendorHoldPayment = (): void => {
    holdPayment.value = null
    // Also clears the in-flight flag: a fetch still running when the merchant
    // is left would otherwise leave the tile stuck on "Checking". Dropping the
    // owner disowns that response too, so it cannot write on arrival.
    holdPaymentLoading.value = false
    inFlightVendorName.value = null
  }

  const executeMerchantAction = async (
    authState: AuthState,
    alertId: number,
    action: ActionRequest,
  ): Promise<void> => {
    try {
      await executeAction(authState, alertId.toString(), action)
      lastActionCompleted.value = action.ACTION_DETAILS
    } catch (error) {
      console.error('Error executing merchant action:', error)
      throw error
    }
  }

  const executeBulkActions = async (
    authState: AuthState,
    actions: BulkActionsRequest,
  ): Promise<void> => {
    try {
      await executeBulkActionsApi(authState, actions)
      // Track last action for any feedback
      if (actions.actions.length > 0) {
        lastActionCompleted.value = actions.actions[0].action_details
      }
    } catch (error) {
      console.error('Error executing bulk actions:', error)
      throw error
    }
  }

  // Whether the NON_ISSUE action type is present in the fetched available actions.
  // Must be true before showing the Non-Issue quick-action button.
  const nonIssueActionAvailable = computed((): boolean =>
    availableActions.value.some(a => a.ACTION_TYPE === ActionType.NON_ISSUE),
  )

  /**
   * Disposition one or more alerts as Non-Issue in a single bulk-actions call.
   * Looks up the NON_ISSUE action type from `availableActions` (must be loaded first).
   * Throws if NON_ISSUE is absent — callers should gate on `nonIssueActionAvailable`.
   */
  const dispositionAsNonIssue = async (
    authState: AuthState,
    alertIds: number[],
  ): Promise<void> => {
    const nonIssueAction = availableActions.value.find(
      a => a.ACTION_TYPE === ActionType.NON_ISSUE,
    )
    if (!nonIssueAction) {
      throw new Error(
        'NON_ISSUE action type is not available. Ensure fetchAvailableActions has been called.',
      )
    }
    const request: BulkActionsRequest = {
      actions: alertIds.map(alertId => ({
        alert_id: alertId,
        action_type_id: nonIssueAction.ACTION_TYPE_ID,
        action_details: nonIssueAction.DESCRIPTION,
      })),
    }
    try {
      await executeBulkActionsApi(authState, request)
      lastActionCompleted.value = nonIssueAction.DESCRIPTION
    } catch (error) {
      console.error('Error dispositioning alerts as Non-Issue:', error)
      throw error
    }
  }

  const clearCurrentMerchant = (): void => {
    currentMerchantId.value = null
    showMerchantDetails.value = false
    selectedMerchant.value = null
  }

  const clearMerchants = (): void => {
    merchants.value = []
    currentMerchantId.value = null
    showMerchantDetails.value = false
    selectedMerchants.value = []
    selectedMerchant.value = null
  }

  const clearMerchantAlertHistory = (): void => {
    merchantAlertHistory.value = []
    alertHistoryPagination.value = {
      page: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    }
  }

  const setMerchantAlertHistory = (history: AlertHistory[]): void => {
    merchantAlertHistory.value = history
  }

  const updateAlertHistoryPagination = (page: number, pageSize: number): void => {
    alertHistoryPagination.value.page = page
    alertHistoryPagination.value.pageSize = pageSize
  }

  return {
    // State
    merchants,
    loading,
    error,
    filters,
    currentMerchantId,
    currentMerchantName,
    showMerchantDetails,
    isEditModalOpen,
    isEditReservesModalOpen,
    isManageSettlementsModalOpen,
    holdPayment,
    holdPaymentLoading,
    eventTypes,
    selectedMerchants,
    selectedMerchant,
    merchantEventTolerance,
    currentMerchantEventTolerances,
    availableActions,
    lastActionCompleted,
    merchantAlertHistory,
    alertHistoryPagination,
    alertHistoryLoading,
    reserveMetrics,
    reserveMetricsLoading,
    reserveMetricsLoaded,
    reserveMetricsError,

    // Getters
    filteredMerchants,
    currentMerchant,
    getMerchantById,
    nonIssueActionAvailable,

    // Actions
    setMerchants,
    setCurrentMerchantId,
    setShowMerchantDetails,
    setIsEditModalOpen,
    setIsEditReservesModalOpen,
    setIsManageSettlementsModalOpen,
    fetchVendorHoldPayment,
    updateVendorHoldPayment,
    clearVendorHoldPayment,
    setLoading,
    setEventTypes,
    setAvailableActions,
    setSelectedMerchants,
    setSelectedMerchant,
    setMerchantEventTolerance,
    setCurrentMerchantEventTolerances,
    setLastActionCompleted,
    updateFilters,
    resetFilters,
    fetchMerchants,
    fetchMerchantDetails,
    fetchEventTypes,
    fetchAvailableActions,
    updateMerchantTolerance,
    updateMerchantMaxAmount,
    enableMerchantReserve,
    setMerchantReservePersona,
    setMerchantReserveManual,
    updateMerchantPercentHoldback,
    fetchMerchantReserveMetrics,
    clearReserveMetrics,
    fetchMerchantAlertHistory,
    fetchMerchantEventTolerances,
    updateMerchantEventToleranceValue,
    executeMerchantAction,
    executeBulkActions,
    dispositionAsNonIssue,
    clearCurrentMerchant,
    clearMerchants,
    clearMerchantAlertHistory,
    setMerchantAlertHistory,
    updateAlertHistoryPagination,
  }
})
